import { mountainCodeFromPath, mountainHubHref } from "./hub-nav";

// The 30 live mountains, captured from the header "Resorts sitemap" nav 2026-07-19. Shared by the
// header variants (home hamburger menu + content-page "Select your mountain" selector).
//
// ⚠ `href` is LIVE's OWN resort-sitemap destination (`/mountain-<code>/profile`, i.e. the directory; only
// Whistler's is the bare hub). It stays exactly as captured because the hidden `resort-sitemap` navs that
// carry it are SEO link equity — repointing them would rewrite the link graph for no ruling. The
// CHANGE-MOUNTAIN DROPDOWN no longer uses it: see `resortHubHref` below (S15 · F89).
export const RESORTS: { href: string; label: string }[] = [
  { href: "/mountain-stowe/profile", label: "Stowe" },
  { href: "/mountain-copper-mt/profile", label: "Copper Mt" },
  { href: "/mountain-steamboat/profile", label: "Steamboat" },
  { href: "/mountain-jackson-hole/profile", label: "Jackson Hole" },
  { href: "/mountain-keystone/profile", label: "Keystone" },
  { href: "/mountain-killington/profile", label: "Killington" },
  { href: "/mountain-palisades/profile", label: "Palisades Tahoe" },
  { href: "/mountain-deer-valley/profile", label: "Deer Valley" },
  { href: "/mountain-mammoth/profile", label: "Mammoth" },
  { href: "/mountain-heavenly/profile", label: "Heavenly" },
  { href: "/mountain-snowmass/profile", label: "Snowmass" },
  { href: "/mountain-vail-sky/profile", label: "Vail " },
  { href: "/mountain-breckenridge/profile", label: "Breckenridge" },
  { href: "/mountain-big-sky/profile", label: "Big Sky" },
  { href: "/mountain-park-city/profile", label: "Park City" },
  { href: "/mountain-whitewater/profile", label: "Whitewater" },
  { href: "/mountain-marmot-basin/profile", label: "Marmot Basin" },
  { href: "/mountain-kicking-horse/profile", label: "Kicking Horse" },
  { href: "/mountain-revelstoke/profile", label: "Revelstoke" },
  { href: "/mountain-red/profile", label: "RED Mountain" },
  { href: "/mountain-panorama/profile", label: "Panorama" },
  { href: "/mountain-lake-louis/profile", label: "Lake Louise" },
  { href: "/mountain-fernie-alpine/profile", label: "Fernie" },
  { href: "/mountain-silver-star/profile", label: "Silver Star" },
  { href: "/mountain-sun-peaks/profile", label: "Sun Peaks" },
  { href: "/mountain-banff-sunshine/profile", label: "Banff Sunshine " },
  { href: "/mountain-tremblant/profile", label: "Mont Tremblant" },
  { href: "/mountain-blue/profile", label: "Blue Mountain" },
  { href: "/mountain-big-white/profile", label: "Big White" },
  // S2 — Baldy, information only (lib/info-only-mountains.ts). Not captured from live's sitemap: the engine's
  // roster does not carry it, so this entry is ours and the 30 above stay exactly as captured.
  { href: "/mountain-baldy/profile", label: "Baldy" },
  { href: "/mountain-whistler", label: "Whistler, BC" },
];

// WO-GF-001-P1B-S15 · F89 — FOUNDER RULING R1 (source: founder, 2026-07-22, room-2), verbatim: the
// change-mountain dropdown entries route to `/mountain-[name]` — the MOUNTAIN HUB for the selected
// mountain. NOT live's resort-sitemap destination (which lands 29 of the 30 on `/profile`, the directory),
// and NOT section-preserving.
//
// The slug is never hand-built: it is read back out of the captured href with `mountainCodeFromPath` — the
// same extractor the hub routes and the mountain memory already use — and rendered by `mountainHubHref`,
// the single place that spells the hub route. A RESORTS entry that stopped looking like a mountain path
// would fall through to its captured href rather than fabricate a destination.
//
// Every one of the 30 hubs is a build-served page (`apps/root/mirror/mountain-<code>.port.json`, 30/30
// verified 2026-07-22), so no entry can 404.
export function resortHubHref(href: string): string {
  const code = mountainCodeFromPath(href);
  return code ? mountainHubHref(code) : href;
}

// WO-GF-001-S23 · F125 STANDING RULE (founder, 2026-07-24): mountains WITH ACTIVE PRODUCT sort FIRST in
// every mountain-select list, site-wide. The active set is derived from the marketplace listings (the
// mountains that actually have bookable product) — verified against /api/marketplace/listings 2026-07-24:
// whistler + big-white were recorded as the marketplace-live mountains. Kept as a small constant so the
// SORT is deterministic offline and on every surface that renders the resort list.
//
// S33 · F191 — BIG WHITE REMOVED, and the drift gate is what forced the correction.
// Founder, walking Phase-1B: "only Whistler is actually bookable — I don't think we have real content on
// any other mountain." Investigated against Strapi and he is exactly right: Big White's three served
// listings are `Big White Day Lift Ticket`, `Big White Snowshoe Tour` and `Big White Ski Rental Package`
// (seeds/marketplace.ts LIVE-MKT-3101/3102/3103) — hand-authored context-switch DEMO rows that borrow
// WHISTLER's vendors, and Big White has no live supplier captures to extract from at all. They now carry
// `provenance: "demo"` in the data, so /api/marketplace/bookable no longer counts them and serves
// `["whistler"]` alone.
//
// ⚠ THIS CONSTANT IS A SORT SEED ONLY — it does NOT drive the BOOKABLE chip. S31 · F184 moved that to the
// served signal precisely so a stale constant could not keep making a claim about a mountain. It survives
// here because the list must sort deterministically before any fetch resolves, and `bookability-is-derived-
// not-declared` asserts it still equals served truth — which is how this edit got triggered rather than
// forgotten. When a mountain genuinely goes live, that gate goes red and points here.
export const ACTIVE_PRODUCT_MOUNTAINS = new Set<string>(["whistler"]);

export const isActiveProduct = (href: string): boolean => {
  const code = mountainCodeFromPath(href);
  return !!code && ACTIVE_PRODUCT_MOUNTAINS.has(code);
};

/** RESORTS with the active-product mountains first (stable within each group). The single ordering used by
 *  every change-mountain / mountain-select list so the rule can never be applied inconsistently. */
export function resortsActiveFirst(): { href: string; label: string }[] {
  const active = RESORTS.filter((r) => isActiveProduct(r.href));
  const rest = RESORTS.filter((r) => !isActiveProduct(r.href));
  return [...active, ...rest];
}
